import { useEffect, useRef } from "react";
import { Label } from "./Label";

type AvailabilitySelectOption = {
  id: number;
  label: string;
  inactive?: boolean;
};

type Props = {
  id: string;
  label: string;
  options: AvailabilitySelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  conflictedIds?: number[];
};

/** A selectable room list that can communicate availability before selection. */
export function AvailabilitySelect({ id, label, options, value, onChange, placeholder, disabled, conflictedIds = [] }: Props) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const selected = options.find((option) => option.id === Number(value));
  const conflicts = new Set(conflictedIds);

  useEffect(() => {
    const closeWhenClickingOutside = (event: PointerEvent) => {
      if (detailsRef.current && !detailsRef.current.contains(event.target as Node)) detailsRef.current.open = false;
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && detailsRef.current?.open) {
        detailsRef.current.open = false;
        detailsRef.current.querySelector<HTMLElement>("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", closeWhenClickingOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeWhenClickingOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <details ref={detailsRef} className="group relative" aria-disabled={disabled}>
        <summary id={id} className={`flex h-10 list-none items-center justify-between rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 marker:hidden focus:outline-none focus:ring-2 focus:ring-sky-500 ${disabled ? "cursor-not-allowed bg-slate-100 text-slate-400" : "cursor-pointer hover:border-slate-400"}`}>
          <span>{selected ? `${selected.label}${selected.inactive ? " · inaktiv" : ""}` : placeholder}</span>
          <span className="text-slate-400 transition-transform group-open:rotate-180">⌄</span>
        </summary>
        {!disabled && (
          <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white p-2 shadow-lg">
            <button type="button" onClick={() => { onChange(""); if (detailsRef.current) detailsRef.current.open = false; }} className={`w-full rounded px-2 py-2 text-left text-sm hover:bg-slate-50 ${!value ? "bg-slate-50 font-medium" : ""}`}>{placeholder}</button>
            <div className="max-h-48 overflow-y-auto">
              {options.map((option) => {
                const conflicted = conflicts.has(option.id);
                return (
                  <button key={option.id} type="button" onClick={() => { onChange(String(option.id)); if (detailsRef.current) detailsRef.current.open = false; }} className={`mt-1 flex w-full flex-col rounded px-2 py-2 text-left text-sm hover:bg-slate-50 ${conflicted ? "bg-rose-50 text-rose-900 hover:bg-rose-100" : ""} ${String(option.id) === value ? "ring-1 ring-sky-300" : ""}`}>
                    <span>{option.label}{option.inactive ? " · inaktiv" : ""}</span>
                    {conflicted && <span className="mt-0.5 text-xs text-rose-700">Im gewählten Zeitraum belegt</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </details>
    </div>
  );
}
